import React from 'react';
import ReactDOM from 'react-dom/client';
import { getCustomOptionPriceModifier } from './components/customOptionUtils';
import { getCustomDataExtrasTotalPrice } from './components/petAccessoryUtils';
import { BrowserRouter, Routes, Route, Link, NavLink, useLocation } from "react-router-dom"
import { useState, useEffect } from 'react';
import axios from 'axios';
import apiUrl from './components/Url';
import { isEuOnlyCountry, shippingRowFor } from './components/countries';
import { applyCountryPrices, currencyForCountry, normalizeCountry } from './utils/countryPricing';

import Item from './pages/Item';
import Category from './pages/Category';
import Collection from './pages/Collection';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Payment from './pages/Payment';
import ScrollToTop from './components/ScrollToTop';
import NotFound from './pages/NotFound';
import { pixelPageView, pixelAddToCart } from './components/MetaPixel';
import { trackEvent, getPriceGroup } from './utils/track';
import Dostava from './pages/Dostava';

import './index.css';



import Header from './components/Header';
import HomeScreen from './pages/HomeScreen';
import PersonalneIlustracije from './pages/PersonalneIlustracije';
import RadioniceKeramike from './pages/RadioniceKeramike';
// Poslovni pokloni — stranica postoji (pages/KorporativniPokloni.js), samo je privremeno
// sklonjena sa sajta. Vrati ovaj import i rutu ispod da se opet otvori.
// import KorporativniPokloni from './pages/KorporativniPokloni';
import AllProducts from './pages/AllProducts';
import NewYearProducts from './pages/NewYearProducts';
import Animation from './components/Animation';
import Politika from './pages/Politika';
import Uslovi from './pages/Uslovi';
import MapaProdavnica from './pages/MapaProdavnica';
import Footer from './components/Footer';
import { makeTranslate, LanguageProvider } from './i18n/translations';
import LanguageModal from './components/LanguageModal';
import {
  initLanguageAccess,
  initialLanguage,
  languageForCountry,
  hasChosenLanguage,
  markLanguageChosen,
} from './i18n/languageAccess';


// Language switching is unlocked per-visitor by a ?lang=1 link — see
// ./i18n/languageAccess.js. Everyone else gets the language of their IP country, with no picker.
const LANGUAGE_UNLOCKED = initLanguageAccess();

const root = ReactDOM.createRoot(document.getElementById('root'));

function useCartItems() {
  const initialCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
  const [cartItems, setCartItems] = React.useState(initialCartItems);


  React.useEffect(() => {
    try {
      localStorage.setItem('cartItems', JSON.stringify(cartItems));
    } catch (error) {
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        // localStorage quota exceeded
        console.error('LocalStorage quota exceeded:', error);
        alert(makeTranslate(localStorage.getItem('language') || 'BA')('Your cart has reached its maximum size. Please try again with smaller images.'));
        
        // Try to recover by keeping cart without the last added item
        if (cartItems.length > 0) {
          const reducedCart = cartItems.slice(0, -1);
          setCartItems(reducedCart);
          try {
            localStorage.setItem('cartItems', JSON.stringify(reducedCart));
          } catch (e) {
            console.error('Failed to save reduced cart:', e);
          }
        }
      } else {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  }, [cartItems]);



  const updateCartItems = (newItems) => {
    setCartItems(newItems);
  };




  // Return the state and updater function
  return [cartItems, updateCartItems];



}





function PixelRouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    pixelPageView();
    trackEvent('page_view', { page_path: location.pathname });
  }, [location.pathname]);
  return null;
}

function Main() {
  // Using the custom hook
  const [cartItems, updateCartItems] = useCartItems();
  const [rawProducts, setRawProducts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);


  // Display language is INDEPENDENT of country/currency. One of: BA (Srpski),
  // US (English), RU (Русский). Product content columns are nameBA/nameUS/nameRU.
  // Locked visitors follow their IP country; unlocked ones pick in the popup below.
  const [country, setCountry] = useState('BA');
  // No currency picker: the country decides the currency and, through admin's
  // per-country prices, the price itself.
  const currency = currencyForCountry(country);
  const productsData = React.useMemo(() => applyCountryPrices(rawProducts, country, getPriceGroup()), [rawProducts, country]);
  const [language, setLanguage] = useState(() => initialLanguage(LANGUAGE_UNLOCKED));

  // Ask unlocked visitors once which language they want.
  const [askLanguage, setAskLanguage] = useState(() => LANGUAGE_UNLOCKED && !hasChosenLanguage());

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const chooseLanguage = (lang) => {
    changeLanguage(lang);
    markLanguageChosen();
    setAskLanguage(false);
  };

  // Closing without picking keeps the current language — the header picker stays available.
  const dismissLanguageModal = () => {
    markLanguageChosen();
    setAskLanguage(false);
  };
  const [shippingInfo, setShippingInfo] = useState([]);
  const [allShippingInfo, setAllShippingInfo] = useState([]);
  const [countryDetected, setCountryDetected] = useState(false); // Track if country detection is complete

  const [isCartOpen, setIsCartOpen] = useState(false);

  const persolaneIlustracijeSlike = Array.from({ length: 15 }, (_, index) => `${index + 1}.jpg`);


  const fetchUserCountry = async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) {
        throw new Error("Failed to fetch country data");
      }
      const data = await response.json();
      const countryCode = data.country_code;
      const countryName = data.country_name;

      // Regional and EU visitors get their own country; anyone else keeps theirs too and
      // sees the default EUR price until they pick a shipping country at checkout.
      const determinedCountry = normalizeCountry(countryCode) || "BA";

      setCountry(determinedCountry);
      setLanguage(languageForCountry(countryCode, LANGUAGE_UNLOCKED));
      localStorage.setItem("country", determinedCountry);
      // Store actual country code for stock API (BA, RS, HR, ME)
      localStorage.setItem("apiCountry", countryCode || "BA");
      setCountryDetected(true); // Mark country detection as complete
    } catch (error) {
      console.error("Error fetching country data:", error);
      setCountry("BA");
      localStorage.setItem("country", "BA");
      localStorage.setItem("apiCountry", "BA");
      setCountryDetected(true); // Mark country detection as complete even on error
    }
  };

  useEffect(() => {
    // Always fetch country on page load to detect IP changes
    // This ensures users who travel or use VPN get correct country
    fetchUserCountry();
  }, []);





  useEffect(() => {
    const fetchShippingInfo = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/shipping?country=${country}`);
        setAllShippingInfo(response.data)
        // EU countries without their own row use the fixed EU rate
        setShippingInfo(shippingRowFor(response.data, country) || {})

      } catch (error) {
        console.error('Error fetching shipping info:', error);
      }
    };

    fetchShippingInfo();
  }, [country]);






  // Function to fetch data with optional nocache parameter
  const fetchData = React.useCallback(async (nocache = false) => {
    try {
      // Get country code for stock API (BA, RS, HR, ME)
      const apiCountry = localStorage.getItem("apiCountry") || "BA";
      const nocacheParam = nocache ? '&nocache=true' : '';
      // Get preview key from URL or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      const previewKey = urlParams.get('key') || localStorage.getItem('previewKey') || '';
      const previewParam = previewKey ? `&preview_key=${previewKey}` : '';
      
      // Store preview key in localStorage if found in URL
      if (urlParams.get('key')) {
        localStorage.setItem('previewKey', urlParams.get('key'));
      }
      
      const response = await fetch(`${apiUrl}/api/products?country=${apiCountry}${nocacheParam}${previewParam}`);
      const data = await response.json();
      setRawProducts(data.products);
      setCategories(data.categories);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  React.useEffect(() => {
    // Only fetch products after country detection is complete
    if (!countryDetected) return;
    
    fetchData();
  }, [countryDetected, fetchData]); // Wait for country detection to complete

  // Listen for countdown finished events to refetch data without cache
  React.useEffect(() => {
    const handleCountdownFinished = () => {
      // Refetch data without cache when countdown finishes
      fetchData(true);
    };
    
    const handlePreviewKeyDetected = () => {
      // Refetch data when preview key is detected (to load unpublished products)
      fetchData(true);
    };

    window.addEventListener('countdownFinished', handleCountdownFinished);
    window.addEventListener('previewKeyDetected', handlePreviewKeyDetected);
    return () => {
      window.removeEventListener('countdownFinished', handleCountdownFinished);
      window.removeEventListener('previewKeyDetected', handlePreviewKeyDetected);
    };
  }, [fetchData]);


  function ChangeCountry(country) {
    setCountry(country)
  }



  function ChangeQty(cartKey, newQty) {
    updateCartItems(
      cartItems.map((item) => {
        if (item.cartKey === cartKey) {
          return { ...item, qty: newQty };
        }
        return item;
      })
    );
  }

  function RemoveItemFromCart(cartKey) {
    updateCartItems(prevCart => prevCart.filter(item => (item.cartKey ?? item.id) !== cartKey));
  }

  function addToCart(id, currentQuantity, customOption, customData = null, setData = null) {
    if (customData) {
      const cartKey = `${id}_custom_${Date.now()}`;
      updateCartItems(prevCart => [
        ...prevCart,
        { id, qty: currentQuantity, customOption, customData, setData, cartKey }
      ]);
    } else {
      const cartKey = `${id}_${setData?.label || ''}`;
      const exists = cartItems.some(item => item.cartKey === cartKey);
      if (exists) {
        updateCartItems(prevCart => prevCart.map(item =>
          item.cartKey === cartKey ? { ...item, qty: item.qty + currentQuantity } : item
        ));
      } else {
        updateCartItems(prevCart => [
          ...prevCart,
          { id, qty: currentQuantity, customOption, customData, setData, cartKey }
        ]);
      }
    }
    const product = productsData.find(p => p.id === id);
    pixelAddToCart(product, currentQuantity, currency, customOption, language);
    trackEvent('add_to_cart', { product_id: id });
    setIsCartOpen(true);
  }

  function RemoveLocalStorage() {
    updateCartItems([])
  }


  const SubtotalAmount = cartItems.map(item => {
    const product = productsData.find(data => data.id === item.id);

    if (product && product.available === 1 && product.is_in_stock) {
      // Exclude set items where any sub-product is unavailable
      if (item.setData) {
        const isSetAvailable = (item.setData.items || []).every(setItem => {
          const p = productsData.find(pd => pd.id === setItem.product_id);
          return p && p.available && p.is_in_stock;
        });
        if (!isSetAvailable) return 0;
      }

      // Use set price if a set is selected
      if (item.setData && item.setData.price && item.setData.price[currency] !== undefined) {
        return parseFloat((item.setData.price[currency] * item.qty).toFixed(2));
      }

      // Use discounted price if product is on sale, otherwise use regular price
      let basePrice = product.is_on_sale && product.sale_discount_percentage > 0
        ? product[`discounted_${currency}`]
        : product[currency];

      // Add custom option price modifier if applicable (supports both old and new multi-group format)
      const customOptionModifier = getCustomOptionPriceModifier(product.custom_options, item.customOption, currency);
      if (customOptionModifier) basePrice = parseFloat(basePrice) + customOptionModifier;

      // Add extras price modifier (accessories + optional second pet) if applicable
      const accessoriesModifier = getCustomDataExtrasTotalPrice(item.customData, currency);
      if (accessoriesModifier) basePrice = parseFloat(basePrice) + accessoriesModifier;

      // Round to avoid floating point precision issues
      return parseFloat((basePrice * item.qty).toFixed(2));
    }

    return 0;
  });


  const totalSubtotal = SubtotalAmount.reduce((acc, subtotal) => acc + subtotal, 0);
  
  // The shipping row is priced in its country's currency, which is the currency shown
  let finalShippingPrice = shippingInfo.price ? Math.ceil(shippingInfo.price) : 0;
  if (currency === "KM" && totalSubtotal > 150) {
    finalShippingPrice = 0;
  } else if (currency === "EUR" && totalSubtotal > 75) {
    finalShippingPrice = 0;
  } else if (currency === "RSD" && totalSubtotal > 9000) {
    finalShippingPrice = 0;
  }
  
  const totalAmount = (Number(totalSubtotal) + Number(finalShippingPrice)).toFixed(2);
  console.log(totalAmount)

  // EU countries always have a price: their own row in admin → Shipping, or the fixed EU rate
  const shippingAvailable = !isEuOnlyCountry(country) || Boolean(shippingRowFor(allShippingInfo, country));



  // UI-string translator bound to the active language. Dictionary lives in
  // ./i18n/translations. US returns the English key as-is; missing keys fall
  // back to readable English rather than undefined.
  const translate = makeTranslate(language);









  return (
    <React.StrictMode>
      <BrowserRouter>
        <LanguageProvider language={language} setLanguage={changeLanguage} unlocked={LANGUAGE_UNLOCKED}>
        <PixelRouteTracker />
        <ScrollToTop />
        {askLanguage && (
          <LanguageModal language={language} onChoose={chooseLanguage} onClose={dismissLanguageModal} />
        )}
        <Header currency={currency} translate={translate} language={language} changeLanguage={changeLanguage} ChangeCountry={ChangeCountry} country={country} cartItems={cartItems} productsData={productsData} RemoveItemFromCart={RemoveItemFromCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} totalSubtotal={totalSubtotal} isCartOpen={isCartOpen} setIsCartOpen={setIsCartOpen} categories={categories} />
        <Routes>
          <Route index element={<HomeScreen country={language} apiCountry={country} translate={translate} currency={currency} productsData={productsData} addToCart={addToCart} cartItems={cartItems} categories={categories} persolaneIlustracijeSlike={persolaneIlustracijeSlike} />} />
          <Route path="/about" element={<HomeScreen addToCart={addToCart} cartItems={cartItems} categories={categories} />} />
          <Route path="/personalne-ilustracije" element={<PersonalneIlustracije categories={categories} persolaneIlustracijeSlike={persolaneIlustracijeSlike} />} />
          <Route path="/radionice-keramike" element={<RadioniceKeramike categories={categories} />} />
          {/* Poslovni pokloni — privremeno sklonjeno; vidi import na vrhu fajla.
          <Route path="/korporativni-pokloni" element={<KorporativniPokloni categories={categories} />} /> */}
          <Route path='/cart' element={<Cart currency={currency} country={language} apiCountry={country} translate={translate} totalAmount={totalAmount} shippingInfo={shippingInfo} shippingPrice={finalShippingPrice} productsData={productsData} cartItems={cartItems} RemoveItemFromCart={RemoveItemFromCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} totalSubtotal={totalSubtotal} categories={categories} />} />
          <Route path='/checkout' element={<Checkout translate={translate} setCountry={setCountry} currency={currency} country={country} shippingInfo={shippingInfo} shippingPrice={finalShippingPrice} RemoveLocalStorage={RemoveLocalStorage} productsData={productsData} cartItems={cartItems} RemoveItemFromCart={RemoveItemFromCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} totalSubtotal={totalSubtotal} categories={categories} apiCountry={country} shippingAvailable={shippingAvailable} />} />
          <Route path="/placanje/:orderId" element={<Payment translate={translate} country={country} currency={currency} productsData={productsData} categories={categories} RemoveLocalStorage={RemoveLocalStorage} />} />
          <Route path="/dostava" element={<Dostava categories={categories} />} />
          <Route path="/mapa-prodavnica" element={<MapaProdavnica categories={categories} />} />
          <Route path="/politika-privatnosti" element={<Politika categories={categories} />} />
          <Route path="/uslovi-koriscenja" element={<Uslovi categories={categories} />} />
          <Route path="/proizvod/:categoryName/:productPath" element={<Item allShippingInfo={allShippingInfo} categories={categories} country={language} apiCountry={country} translate={translate} currency={currency} productsData={productsData} cartItems={cartItems} addToCart={addToCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} />} />
          <Route path="/kategorija/:categoryName" element={<Category country={language} translate={translate} currency={currency} productsData={productsData} cartItems={cartItems} addToCart={addToCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} categories={categories} />} />
          <Route path="/kolekcije/sve" element={<AllProducts country={language} apiCountry={country} translate={translate} currency={currency} productsData={productsData} cartItems={cartItems} addToCart={addToCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} categories={categories} />} />
          <Route path="/kolekcije/novogodisnja" element={<NewYearProducts country={language} apiCountry={country} translate={translate} currency={currency} productsData={productsData} cartItems={cartItems} addToCart={addToCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} categories={categories} />} />
          <Route path="/kolekcije/:collectionUrl" element={<Collection country={language} translate={translate} currency={currency} productsData={productsData} cartItems={cartItems} addToCart={addToCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} categories={categories} />} />
          <Route path="/kolekcija/:collectionUrl" element={<Collection country={language} translate={translate} currency={currency} productsData={productsData} cartItems={cartItems} addToCart={addToCart} setCartItems={updateCartItems} ChangeQty={ChangeQty} categories={categories} />} />

          {/* 404 route - this will match any route that hasn't been matched so far */}
          <Route path="*" element={<NotFound categories={categories} />} />

        </Routes>
        </LanguageProvider>

      </BrowserRouter>
    </React.StrictMode>
  );
}

root.render(<Main />);

